/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useState, useEffect } from 'react';
import {
  Button,
  Checkbox,
  IconChevronRight,
  IconClose,
  Input,
  Modal,
  Select,
  Spinner,
  Switch,
} from 'takeat-design-system-ui-kit';
import toast from 'react-hot-toast';
import { useAuth } from '~/context/AuthContext';
import { usePrinterData } from './hooks/usePrinterData';
import { useProductSelection } from './hooks/useProductSelection';
import './PrinterModal.scss';

const printerWidths = [
  { value: 58, label: '58mm' },
  { value: 80, label: '80mm' },
];

export const PrinterModal = ({
  open,
  onClose,
  isEdit = false,
  printerData,
  onPrinterUpdate,
  onPrinterCreate,
}) => {
  const { user, token } = useAuth();
  const [name, setName] = useState(printerData?.name || '');
  const [host, setHost] = useState(printerData?.host || '');
  const [width, setWidth] = useState(printerData?.width || 80);
  const [leftMargin, setLeftMargin] = useState(printerData?.margin_left || 0);
  const [limitPrintProduct, setLimitPrintProduct] = useState(
    printerData?.use_products || false
  );
  const [printCommand, setPrintCommand] = useState(
    printerData?.print_bills_allowed || false
  );
  const [printOrders, setPrintOrders] = useState(
    printerData?.print_orders_allowed || false
  );
  const [printSelfService, setPrintSelfService] = useState(
    printerData?.print_self_service_allowed || false
  );
  const [categoryExpanded, setCategoryExpanded] = useState(null);

  const {
    products: arrayProducts,
    waiters,
    defaultWaiter,
    printerNames,
    printerNamesLoading,
    waitersLoading,
    productsLoading,
    saveLoading,
    setDefaultWaiter,
    savePrinter,
  } = usePrinterData(printerData, isEdit, token);

  const {
    products: selectedProducts,
    overallSelectionState,
    getCategoryState,
    toggleAll,
    toggleCategory,
    toggleProduct,
    getSelectedProductIds,
    updateProducts,
  } = useProductSelection(arrayProducts);

  useEffect(() => {
    updateProducts(arrayProducts);
  }, [arrayProducts, updateProducts]);

  function toggleExpand(id) {
    if (categoryExpanded === id) {
      setCategoryExpanded(null);
    } else {
      setCategoryExpanded(id);
    }
  }

  const handleSave = async () => {
    if (!name || !host) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    const arrayProductsId = getSelectedProductIds();
    const printerPayload = {
      use_products: limitPrintProduct,
      products: arrayProductsId,
      print_bills_allowed: printCommand,
      print_orders_allowed: printOrders,
      print_self_service_allowed: printSelfService,
      waiter_id: defaultWaiter !== null ? defaultWaiter?.value : null,
      margin_left: Number(leftMargin),
      width: Number(width),
      name,
      ...(isEdit ? {} : { host }),
    };
    try {
      const response = await savePrinter(printerPayload);
      if (isEdit && onPrinterUpdate && response) {
        onPrinterUpdate(response);
      }
      if (!isEdit && onPrinterCreate) {
        onPrinterCreate();
      }
      onClose();
      toast.success(
        isEdit
          ? 'Impressora salva com sucesso'
          : 'Impressora cadastrada com sucesso'
      );
    } catch (error) {
      console.error('Error saving printer:', error);
    }
  };

  return (
    <Modal
      open={open}
      toggle={onClose}
      style={{
        height: 'auto',
        width: 800,
        maxWidth: 800,
        position: 'relative',
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}
    >
      <div
        style={{
          position: 'absolute',
          right: 16,
          top: 16,
          cursor: 'pointer',
        }}
      >
        <IconClose fontSize={24} fill="#545454" onClick={onClose} />
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          height: 48,
        }}
      >
        <p className="text-large text__semi-bold">
          {isEdit ? 'Editar Impressora' : 'Cadastro de impressora'}
        </p>
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <Input
            containerStyles={{ flex: 1 }}
            className="small-input"
            label="Nome no sistema Takeat"
            placeholder=""
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Select
            containerStyles={{ flex: 1 }}
            className="small-select-input"
            label={
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                Impressora
                {printerNamesLoading && <Spinner size={12} color="#666" />}
              </div>
            }
            placeholder=""
            value={{ value: host, label: host }}
            options={printerNames}
            isDisabled={isEdit || printerNamesLoading}
            onChange={(e) => {
              if (e) setHost(e.value);
            }}
          />
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <Select
            containerStyles={{ flex: 1 }}
            className="small-select-input"
            label="Largura na impressão"
            placeholder=""
            options={printerWidths}
            value={{ value: width, label: `${width}mm` }}
            // @ts-ignore
            onChange={(e) => setWidth(e.value)}
          />
          <Input
            type="number"
            step="1"
            containerStyles={{ flex: 1 }}
            className="small-input"
            label="Margem esquerda"
            placeholder=""
            value={leftMargin}
            onChange={(e) => setLeftMargin(e.target.value)}
          />
        </div>
      </div>
      {isEdit && (
        <>
          <div className="printer-modal__container-switchs">
            <>
              <Switch
                label="Imprime Comanda"
                checked={printCommand}
                onChange={(e) => setPrintCommand(e.target.checked)}
              />
              <Switch
                label="Imprime Pedidos"
                checked={printOrders}
                onChange={(e) => setPrintOrders(e.target.checked)}
              />
              <Switch
                label="Imprime Autoatendimento"
                onChange={(e) => setPrintSelfService(e.target.checked)}
                defaultChecked={printSelfService}
              />
            </>
            {printSelfService && (
              <>
                {waitersLoading ? (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '48px',
                      border: '1px solid #EDEDED',
                      borderRadius: '8px',
                      backgroundColor: '#f9f9f9',
                    }}
                  >
                    <Spinner size={20} color="#666" />
                  </div>
                ) : (
                  defaultWaiter !== null && (
                    <Select
                      label="Associar garçom"
                      value={defaultWaiter}
                      options={waiters}
                      placeholder="Selecione o garçom"
                      onChange={(e) => {
                        setDefaultWaiter(e);
                      }}
                    />
                  )
                )}
              </>
            )}
          </div>
          <div className="printer-modal__items-menu-container">
            <span className="printer-modal__title">Itens do cardápio</span>
            <Switch
              onChange={(e) => {
                setLimitPrintProduct(e.target.checked);
              }}
              checked={limitPrintProduct}
              label="Limitar impressão de categorias e/ou produtos específicos"
            />
            {limitPrintProduct && (
              <div className="printer-modal__all-items-container">
                <div className="printer-modal__all-items">
                  <div className="printer-modal__header-all-items">
                    <div
                      className={`printer-modal__checkbox-wrapper printer-modal__checkbox-wrapper--header ${
                        overallSelectionState === 'partial'
                          ? 'printer-modal__checkbox-wrapper--indeterminate'
                          : ''
                      }`}
                      role="button"
                      tabIndex={0}
                      onClick={toggleAll}
                    >
                      <Checkbox
                        checked={overallSelectionState === 'all'}
                        readOnly
                      />
                      Todos os itens
                    </div>
                  </div>
                  <div className="printer-modal__items-list">
                    {productsLoading ? (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: '200px',
                          border: '1px solid #EDEDED',
                          borderRadius: '8px',
                          backgroundColor: '#f9f9f9',
                        }}
                      >
                        <Spinner size={24} color="#666" />
                      </div>
                    ) : (
                      selectedProducts
                        .filter(
                          (category) =>
                            category.products && category.products.length > 0
                        )
                        .map((category) => {
                          const categoryCheckState = getCategoryState(
                            category.id
                          );
                          return (
                            <React.Fragment key={category?.id}>
                              <div className="printer-modal__category-row">
                                <div className="printer-modal__header-product">
                                  <div
                                    className="printer-modal__chevron-clickable-area"
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => {
                                      toggleExpand(category?.id);
                                    }}
                                  >
                                    <IconChevronRight
                                      fontSize={20}
                                      style={{
                                        transition: 'all 0.2s',
                                        transform:
                                          categoryExpanded === category?.id
                                            ? 'rotate(90deg)'
                                            : 'rotate(0)',
                                      }}
                                    />
                                  </div>
                                  <div
                                    className={`printer-modal__checkbox-wrapper printer-modal__checkbox-wrapper--category ${
                                      categoryCheckState.indeterminate
                                        ? 'printer-modal__checkbox-wrapper--indeterminate'
                                        : ''
                                    }`}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => {
                                      toggleCategory(category?.id);
                                    }}
                                  >
                                    <Checkbox
                                      checked={categoryCheckState.checked}
                                      readOnly
                                    />
                                    {category?.name}
                                  </div>
                                </div>
                              </div>

                              {category?.products &&
                                category?.products.map((productItem) => (
                                  <div
                                    key={`product-${productItem.id}`}
                                    className="printer-modal__product-row"
                                    style={{
                                      display:
                                        categoryExpanded !== category.id &&
                                        'none',
                                    }}
                                  >
                                    <div
                                      className="printer-modal__checkbox-wrapper printer-modal__checkbox-wrapper--product"
                                      role="button"
                                      tabIndex={0}
                                      onKeyDown={(e) => {
                                        if (
                                          e.key === 'Enter' ||
                                          e.key === ' '
                                        ) {
                                          toggleProduct(
                                            category.id,
                                            productItem.id
                                          );
                                        }
                                      }}
                                      onClick={() =>
                                        toggleProduct(
                                          category.id,
                                          productItem.id
                                        )
                                      }
                                    >
                                      <Checkbox
                                        checked={productItem.checked}
                                        readOnly
                                      />
                                      {productItem.name}
                                    </div>
                                  </div>
                                ))}
                            </React.Fragment>
                          );
                        })
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingTop: 12,
          borderTop: '1px solid #EDEDED',
        }}
      >
        <Button variant="outlined" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          style={{ width: 80 }}
          isLoading={saveLoading}
          disabled={saveLoading}
          type="submit"
          onClick={handleSave}
        >
          Salvar
        </Button>
      </div>
    </Modal>
  );
};
